import { useEffect, useRef } from 'react';
import LinkButton from '@klaviyo-mkt/gatsby-components/atoms/button/variants/link';
import Icon from '@klaviyo-mkt/gatsby-components/atoms/icon';
import { CurriculumList } from '../../shared/curriculum-list';
import { useGetCurriculumList } from '../../hooks/useGetCurriculumList';

import type { ICourse } from '../../../graphql/types';

import * as styles from './styles/lessonTopNav.module.scss';


export const LessonTopNav = ({
  course,
  coursePath,
}: {
  course: ICourse | null;
  coursePath: string;
}) => {
  const curriculumList = useGetCurriculumList(course?.thirdPartyId ?? '');
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!dialog) return;

      const rect = dialog.getBoundingClientRect();
      const isInDialog = (rect.top <= event.clientY && event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX && event.clientX <= rect.left + rect.width);

      if (!isInDialog) {
        dialog.close();
      }
    };

    dialog?.addEventListener('click', handleOutsideClick);

    return () => dialog?.removeEventListener('click', handleOutsideClick);
  }, [dialogRef]);

  return (
    <>
      <div className={styles.lessonTopNav}>
        <button aria-label="Open lesson menu dialog" className={styles.menuButton}
                onClick={() => dialogRef.current?.showModal()}>
          <svg className={styles.icon} width="24" height="18" viewBox="0 0 24 18" fill="none"
               xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="2" rx="1" fill="currentColor" />
            <rect y="8" width="24" height="2" rx="1" fill="currentColor" />
            <rect y="16" width="24" height="2" rx="1" fill="currentColor" />
          </svg>
          <span className="semibold">Lessons</span>
        </button>
      </div>
      <dialog className={styles.lessonDialog} ref={dialogRef}>
        <button aria-label="Close lesson menu dialog" autoFocus className={styles.closeButton}
                onClick={() => dialogRef.current?.close()}>
          <Icon fillColor="currentColor" iconName="X" size={2} />
        </button>
        <h2>{course?.title}</h2>
        <LinkButton leftIcon={{ iconName: 'ArrowLeft' }} text="Course overview" url={coursePath} variant="ghost" />
        {Array.isArray(course?.lessons) ?
          <CurriculumList coursePath={coursePath} curriculumList={curriculumList}
                          lessons={course.lessons} showHeadline={false} /> : null}
      </dialog>
    </>
  );
};
